import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Star, Check, Minus, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PHYSIO_INCLUDES,
  STANDARD_INCLUDES,
  effectivePrice,
  formatPrice,
  type Product,
  type PurchaseType,
} from "@/lib/shop";
import { useCart } from "@/lib/cart";

export function PurchaseOptionDialog({
  product,
  open,
  onOpenChange,
  buyNow,
}: {
  product: Product | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** When true, go straight to checkout after the option is chosen. */
  buyNow?: boolean;
}) {
  const { add, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();
  const [options, setOptions] = useState<Record<string, string>>({});
  const [qty, setQty] = useState(1);

  const variants = useMemo(() => product?.variants ?? [], [product]);

  if (!product) return null;

  const missing = variants.filter((v) => v.options?.length && !options[v.name]).map((v) => v.name);

  function choose(type: PurchaseType) {
    if (!product) return;
    if (missing.length) return;
    add({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      brand: product.brand,
      image: product.images?.[0] ?? null,
      purchaseType: type,
      unitPrice: effectivePrice(product, type),
      options,
      qty,
    });
    onOpenChange(false);
    setOptions({});
    setQty(1);
    if (buyNow) {
      setCartOpen(false);
      navigate({ to: "/shop/checkout" });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-primary">Choose your purchase option</DialogTitle>
          <DialogDescription>{product.name}</DialogDescription>
        </DialogHeader>

        {variants.length > 0 && (
          <div className="space-y-4 border-b border-border pb-4">
            {variants.map((v) => (
              <div key={v.name}>
                <div className="text-xs font-semibold uppercase tracking-wider text-primary/70">{v.name}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(v.options ?? []).map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => setOptions((cur) => ({ ...cur, [v.name]: o }))}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        options[v.name] === o
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-primary hover:bg-accent"
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary/70">Quantity</span>
          <div className="inline-flex items-center rounded-full border border-border">
            <button
              type="button"
              aria-label="Decrease quantity"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="inline-flex h-9 w-9 items-center justify-center rounded-l-full hover:bg-accent"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-9 text-center text-sm font-semibold text-primary">{qty}</span>
            <button
              type="button"
              aria-label="Increase quantity"
              onClick={() => setQty((q) => Math.min(99, q + 1))}
              className="inline-flex h-9 w-9 items-center justify-center rounded-r-full hover:bg-accent"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <OptionCard
            title="Standard Purchase"
            price={effectivePrice(product, "standard")}
            includes={STANDARD_INCLUDES}
            cta="Select Standard"
            onSelect={() => choose("standard")}
            disabled={missing.length > 0}
          />
          {product.physio_purchase_enabled && product.physio_price != null && (
            <OptionCard
              recommended
              title="Physio Guided Purchase"
              price={effectivePrice(product, "physio")}
              includes={["Product", ...PHYSIO_INCLUDES]}
              cta="Select Physio Guided"
              onSelect={() => choose("physio")}
              disabled={missing.length > 0}
            />
          )}
        </div>

        {missing.length > 0 && (
          <p className="text-xs text-muted-foreground">Please choose {missing.join(", ")} to continue.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OptionCard({
  title,
  price,
  includes,
  cta,
  onSelect,
  recommended,
  disabled,
}: {
  title: string;
  price: number;
  includes: string[];
  cta: string;
  onSelect: () => void;
  recommended?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${recommended ? "border-primary/40 bg-secondary/40" : "border-border"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 font-display font-semibold text-primary">
          {recommended && <Star className="h-4 w-4 fill-primary text-primary" />}
          {title}
        </div>
        {recommended && (
          <span className="rounded-full border border-primary/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            Recommended
          </span>
        )}
      </div>
      <ul className="mt-3 space-y-1.5">
        {includes.map((i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            {i}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="font-display text-lg font-bold text-primary">{formatPrice(price)}</div>
        <button
          type="button"
          disabled={disabled}
          onClick={onSelect}
          className="btn-hero inline-flex h-10 items-center rounded-full px-5 text-sm font-semibold disabled:opacity-50"
        >
          {cta}
        </button>
      </div>
    </div>
  );
}
